import { SharedValue, useSharedValue } from 'react-native-reanimated';

export interface IImageViewerSharedValues {
  // Open/close morph rect (drives the MorphOverlay during opening/closing only).
  animLeft: SharedValue<number>;
  animTop: SharedValue<number>;
  animWidth: SharedValue<number>;
  animHeight: SharedValue<number>;
  animOpacity: SharedValue<number>;
  // Background blur opacity (open/close fade).
  bgOpacity: SharedValue<number>;
  // Carousel container opacity. Held at 0 during the open morph then faded in.
  carouselOpacity: SharedValue<number>;
  // Absolute horizontal translation of the 3-pane carousel track, in pixels.
  // At rest: trackTranslateX = -activeIndex * paneWidth (so the slot whose
  // pageIndex matches the active photo sits at screen center). The SV is NEVER
  // reset at commit time — instead, slot pageIndices rotate around an unchanged
  // track position, which keeps the visible center pixel stable across a
  // navigation commit (no flicker).
  trackTranslateX: SharedValue<number>;
  // Snapshot of trackTranslateX at the start of a pan. Used to compute the
  // running translation as `lastTrackTranslateX + event.translationX`, and to
  // resume a pan that began mid-snap.
  lastTrackTranslateX: SharedValue<number>;
  // Vertical translation applied to the entire 3-pane track (used to shift the
  // active image upward when the bottom sheet opens — keeps all 3 panes visually
  // aligned during a swipe with the sheet open).
  trackTranslateY: SharedValue<number>;
  // Active-gesture flags.
  isGestureActive: SharedValue<boolean>;
  isPanning: SharedValue<boolean>;
}

// Centralized hook owning every viewer-level shared value used by the image viewer.
// Per-pane zoom/pan SVs live in usePaneSlots — each pane gets its own bag from a
// fixed pool of 3 so worklets can address the active pane without mounting churn.
export const useImageViewerSharedValues = (): IImageViewerSharedValues => ({
  animLeft: useSharedValue(0),
  animTop: useSharedValue(0),
  animWidth: useSharedValue(0),
  animHeight: useSharedValue(0),
  animOpacity: useSharedValue(0),
  bgOpacity: useSharedValue(1),
  carouselOpacity: useSharedValue(1),
  trackTranslateX: useSharedValue(0),
  lastTrackTranslateX: useSharedValue(0),
  trackTranslateY: useSharedValue(0),
  isGestureActive: useSharedValue<boolean>(false),
  isPanning: useSharedValue<boolean>(false),
});
